if (!customElements.get('embla-banner-slider')) {
  customElements.define(
    'embla-banner-slider',
    class EmblaBannerSlider extends HTMLElement {
      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.destroy();
      }

      init() {
        if (this.dataset.initialized === 'true') return;

        const viewport = this.querySelector('.embla__viewport');
        if (!viewport) return;

        const loop = this.dataset.emblaLoop === 'true';
        const autoplayEnabled = this.dataset.emblaAutoplay === 'true';
        const autoplayDelay =
          this.parseMs(this.dataset.emblaAutoplayDelay, 5) * 1000;

        const tryInit = () => {
          if (typeof window.EmblaCarousel !== 'function') {
            window.setTimeout(tryInit, 50);
            return;
          }

          const hasAutoplayPlugin =
            typeof window.EmblaCarouselAutoplay === 'function';
          const autoplayPlugin =
            autoplayEnabled && hasAutoplayPlugin
              ? window.EmblaCarouselAutoplay({
                  delay: autoplayDelay,
                  stopOnInteraction: false,
                  stopOnMouseEnter: true,
                })
              : null;

          this.embla = window.EmblaCarousel(
            viewport,
            {
              loop,
              align: 'start',
            },
            autoplayPlugin ? [autoplayPlugin] : []
          );

          this.pluginStop = () => autoplayPlugin?.stop?.();
          this.pluginPlay = () =>
            autoplayPlugin?.[autoplayPlugin?.play ? 'play' : 'reset']?.();

          this.controller = new AbortController();
          const { signal } = this.controller;

          this.setActiveUi = () => {
            const selected = this.embla.selectedScrollSnap();
            this.querySelectorAll('[data-embla-dot]').forEach((btn) => {
              const index = Number(btn.getAttribute('data-embla-dot'));
              const isActive = index === selected;
              btn.classList.toggle('slider-counter__link--active', isActive);
              if (isActive) btn.setAttribute('aria-current', 'true');
              else btn.removeAttribute('aria-current');
            });

            const prevBtn = this.querySelector('.embla__arrow--prev');
            const nextBtn = this.querySelector('.embla__arrow--next');
            if (!prevBtn || !nextBtn) return;
            if (loop) {
              prevBtn.disabled = false;
              nextBtn.disabled = false;
              return;
            }
            prevBtn.disabled = !this.embla.canScrollPrev();
            nextBtn.disabled = !this.embla.canScrollNext();
          };

          this.addEventListener(
            'click',
            (event) => {
              const prev = event.target.closest('.embla__arrow--prev');
              if (prev) return this.embla.scrollPrev();
              const next = event.target.closest('.embla__arrow--next');
              if (next) return this.embla.scrollNext();
              const dot = event.target.closest('[data-embla-dot]');
              if (dot) {
                const index = Number(dot.getAttribute('data-embla-dot'));
                if (Number.isFinite(index)) this.embla.scrollTo(index);
              }
            },
            { signal }
          );

          this.addEventListener('focusin', this.pluginStop, { signal });
          this.addEventListener('focusout', this.pluginPlay, { signal });
          document.addEventListener(
            'visibilitychange',
            () => (document.hidden ? this.pluginStop() : this.pluginPlay()),
            { signal }
          );

          // Customizer support: scroll to slide when block is selected
          this.addEventListener(
            'shopify:block:select',
            (event) => {
              const slide = event.target.closest('.embla__slide');
              if (!slide || !this.embla) return;
              const index = Array.from(
                this.querySelectorAll('.embla__slide')
              ).indexOf(slide);
              if (index > -1) {
                this.pluginStop();
                this.embla.scrollTo(index);
              }
            },
            { signal }
          );

          this.addEventListener(
            'shopify:block:deselect',
            () => {
              if (autoplayEnabled) this.pluginPlay();
            },
            { signal }
          );

          this.embla.on('init', this.setActiveUi);
          this.embla.on('select', this.setActiveUi);
          this.setActiveUi();

          this.embla.on('reInit', this.setActiveUi);

          this.dataset.initialized = 'true';
        };

        tryInit();
      }

      destroy() {
        this.pluginStop?.();
        this.controller?.abort?.();
        this.embla?.destroy?.();
        this.dataset.initialized = 'false';
      }

      parseMs(value, fallback) {
        const n = Number(value);
        if (!Number.isFinite(n)) return fallback;
        return Math.max(0, n);
      }
    }
  );
}
